import { AppSettings } from 'config/index';
import cerberusApi from 'factory/CerberusApi';
import cerberusMfaApi from 'factory/MfaAuth';
import { UserDataSingleton } from 'services/UserDataSingleton';
import
{
  OverviewResponse,
  ApplicationsResponse,
  BillingPlansResponse,
  EditResponse,
  SubscribedAppsResponse,
  PhoneNumberInfoResponse,
  OrganizationTeamResponse,
  SearchOrganizationResponse,
  TenantUserInformationResponse,
  TeamResponse,
  MetricsResponse,
  ApplicationResponse,
  AiAgentsPlanResponse
} from 'models/response/Index';

import {
  BaseRequest,
  ContactRequest,
  ContextRequest,
  OverviewRequest,
  ConfigurationRequest,
  SubscriptionRequest,
  TransactionRequest,
  SearchOrganizationRequest,
  CreateOrganizationRequest,
  AccountRequest,
  MetricsRequest,
  MigrateWhatsAppRequest,
  SchedulerRequest,
  OrganizationRequest,
  SetAiAgentsPlanRequest,
  UpdateIncidentDescriptionRequest,
  SendMessageRequest,
} from 'models/request/Index';
import { AccessKeyResponse } from 'models/response/AccessKeyResponse';
import { AccessKeyRequest } from 'models/request/AccessKeyRequest';
import { BuilderRequest } from 'models/request/BuilderRequest';
import { AccountResponse } from 'models/response/AccountResponse';
import { TeamRequest } from 'models/request/TeamRequest';
import { AgentRequest } from 'models/request/AgentRequest';
import { RedisRequest } from 'models/request/RedisRequest';
import { OrleansRequest } from 'models/request/OrleansRequest';
import { TicketRequest } from 'models/request/TicketRequest';
import { RulesRequest } from 'models/request/RulesRequest';
import { ApplicationRequest } from 'models/request/ApplicationRequest';
import { PaymentAccountRequest } from 'models/request/PaymentAccountRequest';
import { CloneRequest } from 'models/request/CloneRequest';
import { ResourceRequest } from 'models/request/ResourceRequest';
import { IncidentRequest } from 'models/request/IncidentRequest';
import { BotHealthPlansRequest } from 'models/request/BotHealthPlansRequest';
import { BotHealthPlansResponse } from 'models/response/BotHealthPlansResponse';
import { AiAgentsPlanRequest } from 'models/request/AiAgentsPlanRequest';
import { AiAgentsAllPlansResponse } from 'models/response/AiAgentsAllPlansResponse';
import { BulkInsertRequest } from 'models/request/BulkInsertRequest';
import { BotHealthWebhookRequest } from 'models/request/BotHealthWebhookRequest';
import { ActiveSsoRequest } from 'models/request/ActiveSsoRequest';
import { ProcedureRequest } from 'models/request/ProcedureRequest';
import { AiStudioAgentPlanRequest } from 'models/request/AiStudioAgentPlanRequest';
import { ResetMfaRequest } from 'models/request/ResetMfaRequest';

const CERBERUS_API_URL = 'CERBERUS_API_URL';
const APP_NAME = 'Cerberus';

const buildApiUrl = async (property: string, pathUrl: string) : Promise<string> =>{
  return `${await AppSettings.get(property)}${pathUrl}`;
}

const injectUserMetadata = (request: BaseRequest) : BaseRequest => {
  request.MetaData = {
    'user.email':  UserDataSingleton.instance.email,
    'user.name':  UserDataSingleton.instance.name,
    'user.ip':  UserDataSingleton.instance.ip,
    'app.name': APP_NAME
  };
  return request;
}

export const getBotHealthPlans = async (request : BotHealthPlansRequest) : Promise<BotHealthPlansResponse> => {
  request.BotIdentifier = request.BotIdentifier.trim();

  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/bothealth/get-plans');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}
export const getOverviewInfo = async (request : OverviewRequest) : Promise<OverviewResponse> => {
  request.BotIdentifier = request.BotIdentifier.trim();
  request.ClusterName = request.ClusterName.trim();

  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Chatbot/Overview');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const setPaymentAccount = async (request : OverviewRequest) : Promise<string> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Chatbot/set-payment-account');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const setTenantOwnerIdentity = async (request : OrganizationRequest) : Promise<string> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Organization/set-tenant-ownerIdentity');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getTeam = async (request : BaseRequest) : Promise<Array<TeamResponse>> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Chatbot/get-team');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getBuilderFlow = async (request: BuilderRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Chatbot/get-flow');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getFlowApplication = async (request: BaseRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Chatbot/get-flow-application');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getTemplate = async (request: BaseRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Chatbot/get-template');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getTemporaryAccessKey = async (request: AccessKeyRequest) : Promise<AccessKeyResponse> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Chatbot/get-temporary-access-key');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getTemporaryAdminAccessKey = async (request: AccessKeyRequest) : Promise<AccessKeyResponse> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Chatbot/get-temporary-admin-access-key');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getContact = async (request: ContactRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Chatbot/get-contact');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getContext = async (request: ContextRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Chatbot/get-context');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getAllContext = async (request: ContextRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Chatbot/get-all-context');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const deleteContext = async (request: ContextRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Chatbot/delete-context');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const setContext = async (request: ContextRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Chatbot/set-context');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getChatbotAccount = async (request: AccountRequest) : Promise<AccountResponse> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Chatbot/get-account');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getAccountFromChatbot = async (request: OverviewRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Chatbot/get-account-chatbot');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getMetrics = async (request: MetricsRequest) : Promise<MetricsResponse> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Chatbot/get-metrics');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getWhatsapSettings = async (request: MigrateWhatsAppRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Chatbot/get-whatsapp-settings');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const migrateWhatsAppSettings = async (request: MigrateWhatsAppRequest) : Promise<string> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Chatbot/migrate-whatsapp-settings');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const scheduler = async (request: SchedulerRequest) : Promise<string> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Chatbot/scheduler');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getApplication = async (request: OverviewRequest) : Promise<ApplicationResponse> => {
  request.BotIdentifier = request.BotIdentifier.trim();
  request.ClusterName = request.ClusterName.trim();

  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Chatbot/get-application');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const setConfiguration = async (request : ConfigurationRequest) : Promise<string> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Configuration/set');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const addConfiguration = async (request : ConfigurationRequest) : Promise<string> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Configuration/add');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const editConfiguration = async (request : ConfigurationRequest) : Promise<string> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Configuration/edit');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const deleteConfiguration = async (request : ConfigurationRequest) : Promise<string> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Configuration/delete');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getConfiguration = async (request : ConfigurationRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Configuration/get');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const searchOrganization = async (request : SearchOrganizationRequest) : Promise<SearchOrganizationResponse> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Organization/search');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getApplications = async (request : SearchOrganizationRequest) : Promise<Array<ApplicationsResponse>> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Organization/get-applications');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getMembers = async (request : SearchOrganizationRequest) : Promise<Array<TenantUserInformationResponse>> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Organization/get-members');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getPlans = async (request : SearchOrganizationRequest) : Promise<Array<BillingPlansResponse>> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Organization/get-plans');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const setSubscription = async (request : SubscriptionRequest) : Promise<string> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Organization/set-subscription');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const processTransaction = async (request : TransactionRequest) : Promise<string> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Organization/process-transaction');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const createOrganization = async (request: CreateOrganizationRequest) => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Organization/create');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getChannels = async () : Promise<Array<string>> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Organization/channels');
  const { data } = await cerberusApi.get(fullUrl);
  return data;
}

export const getBlipSubsidiaries = async () : Promise<Array<string>> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Organization/blip-subsidiaries');
  const { data } = await cerberusApi.get(fullUrl);
  return data;
}

export const getOrganizationTeam = async (request: BaseRequest) : Promise<Array<OrganizationTeamResponse>> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Organization/get-team');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const deleteUserInApplication = async (request: BaseRequest) : Promise<string> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Organization/delete-user');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const addUserInApplication = async (request: BaseRequest) : Promise<string> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Organization/add-user');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getAiAgentsPlan = async (request : AiAgentsPlanRequest) : Promise<AiAgentsPlanResponse> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Organization/get-ai-agents-plan');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getAllAiAgentsPlans = async (request : BaseRequest) : Promise<AiAgentsAllPlansResponse> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Organization/get-all-ai-agent-plans');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const setAiAgentsPlan = async (request : SetAiAgentsPlanRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Organization/set-ai-agents-plan');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getSubscribedApps = async (request : BaseRequest) : Promise<Array<SubscribedAppsResponse>> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Configuration/waba/subscribed-apps');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getPhoneNumberInfo = async (request : BaseRequest) : Promise<PhoneNumberInfoResponse> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Configuration/cloud-api/phone-number');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const wabaEditApp = async (request : BaseRequest) : Promise<SubscribedAppsResponse> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Configuration/waba/edit-apps');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const changePhoneNumberWebhook = async (request : BaseRequest) : Promise<EditResponse> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Configuration/cloud-api/phone-number/set-webhook');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const calculateActiveCampaign = async (request : BaseRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/ActiveCampaigns/calculate-time');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getActiveCampaignsStatus = async (request : BaseRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/ActiveCampaigns/status-whatsapp');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getActiveCampaignsDeliveryReport = async (request : BaseRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/ActiveCampaigns/status-whatsapp/delivery-report');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getCaller = async (request : ConfigurationRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Desk/get-caller');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getAgentsByTeam = async (request : TeamRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Desk/get-agents-byteam');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getAgents = async (request : AgentRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Desk/get-agents');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getAgentsMetrics = async (request : AgentRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Desk/get-agents-metrics');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const setAgents = async (request : AgentRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Desk/set-agents');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const resyncRedisDistribution = async (request : RedisRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Desk/resync-redis-distribution');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getSyncStateRedisDistribution = async (request : RedisRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Desk/get-state-redis-distribution');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const restartOrleansDistributionGrains = async (request : OrleansRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Desk/restart-orleans-distribution');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getTicketsMetrics = async (request : TicketRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Desk/get-tickets-metrics');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getTicketsInformation = async (request : TicketRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Desk/get-tickets-information');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const changeTicketStatus = async (request : TicketRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Desk/change-ticket-status');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getOpenTickets = async (request : TicketRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Desk/get-open-tickets');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getWaitingTickets = async (request : TicketRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Desk/get-waiting-tickets');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getRules = async (request : RulesRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Desk/get-rules');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getCloneAccount = async (request : AccountRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Clone/get-account');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getCloneApplication = async (request: ApplicationRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Clone/get-application');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getClonePaymentAccount = async (request: PaymentAccountRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Clone/get-payment-acccount');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getCloneCaller = async (request : CloneRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Clone/get-caller');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const cloneBucket = async (request: BuilderRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Clone/clone-bucket');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getBucket = async (request: BuilderRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Clone/get-bucket');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getHttpEnabled = async (request: BuilderRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Clone/get-http-enabled');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getBuilderEnabled = async (request: BuilderRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Clone/get-builder-enabled');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const cloneResource = async (request: ResourceRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Clone/clone-resource');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getResources = async (request: ResourceRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Clone/get-resources');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getIntentions = async (request: CloneRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Clone/get-intentions');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const cloneIntentions = async (request: CloneRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Clone/clone-intentions');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getEntities = async (request: CloneRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Clone/get-entities');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const cloneEntities = async (request: CloneRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Clone/clone-entities');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getIncident = async (request: IncidentRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/StatusPage/get-incident');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getIncidentsList = async (request: BaseRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/StatusPage/get-list-incidents');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const updateInicident = async (request: IncidentRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/StatusPage/update-incident');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const createInicident = async (request: IncidentRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/StatusPage/create-incident');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const deleteIncident = async (request: IncidentRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/StatusPage/delete-incident');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const bulkInsert = async (request: BulkInsertRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Organization/bulk-insert');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const updateIncidentDescription = async (request: UpdateIncidentDescriptionRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/StatusPage/update-incident-description');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const validateWebhook = async (request: BotHealthWebhookRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, `/api/bothealth/check-webhook?wabaId=${request.WabaId}&clusterName=${request.ClusterName}`);
  const { data } = await cerberusApi.get(fullUrl);
  return data;
}

export const activeSso = async (request: ActiveSsoRequest) => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/sso/activate-sso');
  const data = await cerberusApi.post(fullUrl, injectUserMetadata(request))
  return data;
}

export const restartSsoWebApp = async () => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/sso/restart-webapp');
  const data = await cerberusApi.post(fullUrl, null, {
    headers: { 'X-Use-Api-Management': 'true' }
  })
  return data;
}

export const getMtlsDetails = async (request): Promise<Array<object>> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Organization/get-mtls-certificates');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const activeMtlsCertificate = async (request): Promise<Array<object>> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Organization/set-active-mtls-certificates');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const resetMfaMethod = async (request: ResetMfaRequest): Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/mfa/oath-authentication');
  const { data } = await cerberusMfaApi.post(fullUrl, injectUserMetadata(request));
  return data;
};

export const sendMessage = async (request: SendMessageRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/alert/send-alert');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getGroupsConfig = async () : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/alert/groups-config');
  const { data } = await cerberusApi.get(fullUrl);
  return data;
}

export const executeProcedure = async (request: ProcedureRequest) => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Procedure/execute-procedure');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getStudioAgentPlan = async (request: AiStudioAgentPlanRequest) => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Organization/get-studio-agent-plan');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getStudioAgentPlanTiers = async (request: AiStudioAgentPlanRequest) => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Organization/get-studio-agent-plan-tiers');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const updateStudioAgentPlan = async (request: AiStudioAgentPlanRequest) => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Organization/update-studio-agent-plan');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const setStudioAgentPlanTrial = async (request: AiStudioAgentPlanRequest) => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Organization/set-studio-agent-plan-trial');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getStudioStatusPlan = async (request: AiStudioAgentPlanRequest) => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Organization/get-studio-status-plan');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const setBlockAccount = async (request: AccountRequest) => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Chatbot/set-block-account');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const deleteBlockAccount = async (request: AccountRequest) => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Chatbot/delete-block-account');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getCopilotConfiguration = async (request: ConfigurationRequest) => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Configuration/get-copilot');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const setCopilotConfiguration = async (request: ConfigurationRequest) => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/Configuration/set-copilot');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}

export const getAidraRecommendation = async (request: BaseRequest) : Promise<any> => {
  const fullUrl = await buildApiUrl(CERBERUS_API_URL, '/api/cloud/aidra/get-recommendation');
  const { data } = await cerberusApi.post(fullUrl, injectUserMetadata(request));
  return data;
}